import { motion } from 'framer-motion';
import { HardDrive, Diamond } from 'lucide-react';
import { VAULT_DATA } from '@/components/vaultData';
import FolderTree from '@/components/FolderRow';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function VaultPreview() {
  return (
    <section
      data-testid="vault-preview-section"
      className="relative py-20 sm:py-28"
      style={{ background: '#FFF7CD' }}
    >
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 sm:px-8">
        <motion.span
          custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="uppercase text-xs tracking-[0.25em] font-bold text-[#c88a5e] mb-4 block font-body"
          data-testid="vault-overline"
        >
          <Diamond className="inline-block w-3 h-3 mr-2 -mt-0.5" />
          Inside The Vault
        </motion.span>

        <motion.h2
          custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="font-heading font-bold text-[#111] tracking-tight mb-4"
          style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
          data-testid="vault-headline"
        >
          What's on the Drive
        </motion.h2>

        <motion.p
          custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="text-[#777] text-base md:text-lg font-body max-w-xl mb-10"
          data-testid="vault-description"
        >
          Every folder, every file — production-grade STLs organized for instant use.
        </motion.p>

        <motion.div
          custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="card-elevated-lg overflow-hidden"
          data-testid="vault-explorer"
        >
          {/* Title bar */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', background: '#FAFAFA' }}>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#FEBC2E' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#28C840' }} />
            </div>
            <div className="flex items-center gap-2 ml-3">
              <HardDrive className="w-3.5 h-3.5 text-[#FDC3A1]" />
              <span className="font-mono text-xs text-[#666]">DesignCarat_Master_Drive</span>
              <span className="text-[#ccc] text-xs">/</span>
              <span className="font-mono text-xs text-[#999]">{VAULT_DATA.size}</span>
            </div>
            <span className="ml-auto font-mono text-xs text-[#c88a5e] tabular-nums" data-testid="vault-total-files">
              4,000+ STL files
            </span>
          </div>

          {/* Column headers */}
          <div className="flex items-center gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[#aaa] font-mono font-bold" style={{ borderBottom: '1px solid rgba(0,0,0,0.04)', background: '#FCFCFC' }}>
            <span className="w-4" />
            <span className="w-4" />
            <span className="flex-1">Name</span>
            <span className="w-24 text-center">Items</span>
            <span className="w-20 text-right">Size</span>
          </div>

          <FolderTree />

          {/* Status bar */}
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)', background: '#FAFAFA' }}>
            <span className="font-mono text-[10px] text-[#aaa]">{VAULT_DATA.children.length} folders &bull; Ready to download</span>
            <span className="font-mono text-[10px] text-[#c88a5e]">.STL / .OBJ / .STEP</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
